/**
 * Suggested-edits API client (Block 6.C.2 / ADR-021).
 *
 * Mirrors api/_shared/repos/suggestedEdits.ts wire shapes. Submit /
 * approve / reject / withdraw fire `dispatchEmail` server-side as part
 * of the same transaction (atomicity per ADR-022 §7); the client just
 * sees the JSON return value.
 *
 * §allowlist mirror: ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE duplicates the
 * server-side allowlist at api/_shared/repos/suggestedEdits.ts. The UI
 * gates the "Suggest" affordance to only the allowlisted fields per
 * target type; the server is the security boundary regardless.
 *
 * §m: keep the allowlist in sync with the server-side definition. If a
 * new field is added to the server allowlist, add it here too. No test
 * catches this drift; this comment is the binding (mirror of the
 * src/lib/lookups.ts:253-258 DATA_STEWARD_ROLES discipline).
 */

import { writeFetch } from './writeFetch';
import type {
  SuggestedEditCountResponse,
  SuggestedEditListResponse,
  SuggestedEditRow,
  SuggestedEditStatus,
  SuggestedEditTargetType,
  SuggestedEditWithJoinDisplay,
} from './types';
import { apiErrorFrom } from './api';

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function getJson<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `GET ${path}`);
  return (await r.json()) as T;
}

async function postJson<T>(
  slug: string,
  path: string,
  body: object,
  signal?: AbortSignal,
): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `POST ${path}`);
  return (await r.json()) as T;
}

// =====================================================================
// Allowlist (mirror of server-side suggestedEdits.ts)
// =====================================================================

export const ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE: Record<SuggestedEditTargetType, ReadonlyArray<string>> = {
  organization: [
    'name',
    'phone',
    'email',
    'address',
    'website',
    'is_active',
    // Phase 1.7 A5 cohort (migration 0058) — mirror of server-side
    // ORG_ALLOWED_FIELDS in api/_shared/repos/suggestedEdits.ts.
    // §m sync-discipline: update both sides when adding fields.
    // Phase 1.7.1 (migration 0059): `referral_partner` dropped here too;
    // relocated to provider_individuals.is_referral_partner.
    // Phase 1.7.2 (migration 0060): `notes` dropped here too — legacy
    // column gone; notes-write flow via CmWriteNoteForm.
    'medicaid',
    'dementia_unit',
    'phone_2',
    'fax',
    'payment_types',
    'service_delivery_model',
    // Phase 1.9.3 — gender_restrictions added in migration 0068.
    // Enum-via-text: "Women Only" | "Men Only" | NULL.
    'gender_restrictions',
    // Item 5 — Subcategories / Specialties are M:N junctions, not columns.
    // Suggested via a `{ add, remove }` slug delta (server applies it via
    // applyOrgJunctionDelta, not the scalar column path). Byte-mirror of
    // server ORG_ALLOWED_FIELDS in api/_shared/repos/suggestedEdits.ts.
    'subcategories',
    'specialties',
  ],
  individual: [
    'full_name',
    'title',
    'business_phone',
    'cell_phone',
    // A person's fax (0242): mirror of server INDIVIDUAL_ALLOWED_FIELDS.
    'fax',
    'email',
    'address',
    'specialties',
    'notes',
    'is_active',
    // Phase 1.7.1 — referral_partner relocated to the individual side.
    'is_referral_partner',
  ],
};

export function canSuggestEditOnField(
  targetType: SuggestedEditTargetType,
  fieldName: string,
): boolean {
  return ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE[targetType].includes(fieldName);
}

// =====================================================================
// suggestedEditsApi
// =====================================================================

export const suggestedEditsApi = {
  submit(
    slug: string,
    body: {
      target_type: SuggestedEditTargetType;
      target_id: string;
      field_name: string;
      old_value: string | null;
      new_value: string | null;
      rationale: string | null;
    },
    signal?: AbortSignal,
  ): Promise<SuggestedEditRow> {
    return postJson(slug, '/suggested-edits', body, signal);
  },

  list(
    slug: string,
    params: {
      status?: SuggestedEditStatus;
      suggested_by?: string;
      target_type?: SuggestedEditTargetType;
      target_id?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<SuggestedEditListResponse> {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    if (params.suggested_by) sp.set('suggested_by', params.suggested_by);
    if (params.target_type) sp.set('target_type', params.target_type);
    if (params.target_id) sp.set('target_id', params.target_id);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.offset !== undefined) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return getJson(slug, `/suggested-edits${qs ? `?${qs}` : ''}`, signal);
  },

  count(
    slug: string,
    params: {
      status?: SuggestedEditStatus;
      suggested_by?: string;
      target_type?: SuggestedEditTargetType;
      target_id?: string;
    } = {},
    signal?: AbortSignal,
  ): Promise<SuggestedEditCountResponse> {
    const sp = new URLSearchParams();
    if (params.status) sp.set('status', params.status);
    if (params.suggested_by) sp.set('suggested_by', params.suggested_by);
    if (params.target_type) sp.set('target_type', params.target_type);
    if (params.target_id) sp.set('target_id', params.target_id);
    const qs = sp.toString();
    return getJson(slug, `/suggested-edits/count${qs ? `?${qs}` : ''}`, signal);
  },

  get(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<SuggestedEditWithJoinDisplay> {
    return getJson(slug, `/suggested-edits/${encodeURIComponent(id)}`, signal);
  },

  approve(
    slug: string,
    id: string,
    body: { review_note: string | null } = { review_note: null },
    signal?: AbortSignal,
  ): Promise<SuggestedEditRow> {
    return postJson(
      slug,
      `/suggested-edits/${encodeURIComponent(id)}/approve`,
      body,
      signal,
    );
  },

  reject(
    slug: string,
    id: string,
    body: { review_note: string | null } = { review_note: null },
    signal?: AbortSignal,
  ): Promise<SuggestedEditRow> {
    return postJson(
      slug,
      `/suggested-edits/${encodeURIComponent(id)}/reject`,
      body,
      signal,
    );
  },

  withdraw(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<SuggestedEditRow> {
    return postJson(
      slug,
      `/suggested-edits/${encodeURIComponent(id)}/withdraw`,
      {},
      signal,
    );
  },
};
